import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

class NotificationService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.isInitialized = false;
    this.init();
  }

  async init() {
    if (this.isNative) {
      await this.setupPushNotifications();
      await this.setupLocalNotifications();
      this.isInitialized = true;
    }
  }

  // Push Notifications Setup
  async setupPushNotifications() {
    try {
      // Request permission for push notifications
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive === 'granted') {
        // Register for push notifications
        await PushNotifications.register();

        // Listen for registration
        PushNotifications.addListener('registration', (token) => {
          console.log('Push registration success, token: ' + token.value);
          this.pushToken = token.value;
        });

        // Listen for registration errors
        PushNotifications.addListener('registrationError', (err) => {
          console.error('Registration error: ', err.error);
        });

        // Listen for push notifications received
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push notification received: ', notification);
          // Handle notification when app is in foreground
        });

        // Listen for push notification action performed
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Push notification action performed', notification.actionId, notification.inputValue);
          // Handle notification tap/action
        });
      }
    } catch (error) {
      console.log('Push notifications setup error:', error);
    }
  }

  // Local Notifications Setup
  async setupLocalNotifications() {
    try {
      // Request permission for local notifications
      const permission = await LocalNotifications.requestPermissions();
      
      if (permission.display === 'granted') {
        console.log('Local notifications permission granted');

        // Listen for local notification actions
        LocalNotifications.addListener('localNotificationReceived', (notification) => {
          console.log('Local notification received: ', notification);
        });

        LocalNotifications.addListener('localNotificationActionPerformed', (notificationAction) => {
          console.log('Local notification action performed', notificationAction);
        });
      }
    } catch (error) {
      console.log('Local notifications setup error:', error);
    }
  }

  // Schedule Event Reminder (24 hours before)
  async scheduleEventReminder(event) {
    if (!this.isNative) return { success: false, error: 'Not on native platform' };

    try {
      let eventDate;
      
      // Determine event date based on calendar type
      if (event.calendar_type === 'gregorian') {
        eventDate = new Date(event.gregorian_date);
      } else {
        // For Devimana events, use current date as fallback
        // In a real implementation, you'd convert Devimana date to Gregorian
        eventDate = new Date();
        eventDate.setDate(eventDate.getDate() + 1); // Tomorrow as example
      }
      
      // Calculate reminder time (24 hours before)
      const reminderTime = new Date(eventDate.getTime() - (24 * 60 * 60 * 1000));
      
      // Only schedule if reminder time is in the future
      if (reminderTime <= new Date()) {
        console.log('⚠️ Event is too soon for 24-hour reminder');
        return { success: false, error: 'Event is too soon for 24-hour reminder' };
      }

      // Prepare notification body with Tithi info if available
      let notificationBody = `${event.description || 'No description'}`;
      
      if (event.tithi_info) {
        notificationBody += `\n🌙 ${event.tithi_info.tithi_name}`;
        if (event.tithi_info.paksha) {
          notificationBody += ` (${event.tithi_info.paksha})`;
        }
      }
      
      notificationBody += `\n📅 Scheduled for tomorrow`;

      const notificationId = parseInt(event.id) || Date.now();

      await LocalNotifications.schedule({
        notifications: [
          {
            id: notificationId,
            title: `🕉️ Upcoming Event: ${event.title}`,
            body: notificationBody,
            schedule: { at: reminderTime },
            sound: 'default',
            attachments: null,
            actionTypeId: 'event_reminder',
            extra: {
              eventId: event.id,
              eventType: 'reminder',
              calendarType: event.calendar_type,
              eventDate: eventDate.toISOString()
            }
          }
        ]
      });

      console.log(`📱 Event reminder scheduled for "${event.title}" at ${reminderTime.toLocaleString()}`);
      
      return { 
        success: true, 
        reminderTime: reminderTime.toISOString(),
        notificationId: notificationId
      };
      
    } catch (error) {
      console.error('📱 Schedule event reminder error:', error);
      return { success: false, error: error.message };
    }
  }

  // Schedule Daily Sankalpa Reminder
  async scheduleDailySankalpReminder(time = '06:00') {
    if (!this.isNative) return;

    try {
      const [hours, minutes] = time.split(':');
      const now = new Date();
      const scheduleTime = new Date();
      scheduleTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // If time has passed today, schedule for tomorrow
      if (scheduleTime <= now) {
        scheduleTime.setDate(scheduleTime.getDate() + 1);
      }

      await LocalNotifications.schedule({
        notifications: [
          {
            id: 999, // Fixed ID for daily reminder
            title: 'Daily Sankalpa Reminder',
            body: 'Time for your daily Sankalpa meditation 🕉️',
            schedule: {
              at: scheduleTime,
              repeats: true,
              every: 'day'
            },
            sound: 'default',
            actionTypeId: 'sankalpa_reminder',
            extra: {
              type: 'daily_sankalpa'
            }
          }
        ]
      });

      console.log(`Daily Sankalpa reminder scheduled for ${time}`);
    } catch (error) {
      console.log('Schedule daily Sankalpa reminder error:', error);
    }
  }

  // Schedule Tithi Notification
  async scheduleTithiNotification(tithiData) {
    if (!this.isNative) return;

    try {
      const now = new Date();
      const notificationTime = new Date(now.getTime() + (5 * 60 * 1000)); // 5 minutes from now

      await LocalNotifications.schedule({
        notifications: [
          {
            id: Date.now(), // Unique ID based on timestamp
            title: 'Tithi Information',
            body: `Current Tithi: ${tithiData.tithi_name}\nPaksha: ${tithiData.paksha}`,
            schedule: { at: notificationTime },
            sound: 'default',
            actionTypeId: 'tithi_info',
            extra: {
              type: 'tithi_notification',
              tithi: tithiData.tithi_name
            }
          }
        ]
      });

      console.log('Tithi notification scheduled');
    } catch (error) {
      console.log('Schedule tithi notification error:', error);
    }
  }

  // Cancel Event Reminder
  async cancelEventReminder(eventId) {
    if (!this.isNative) return;

    try {
      await LocalNotifications.cancel({
        notifications: [{ id: parseInt(eventId) }]
      });
      console.log(`Event reminder cancelled for event ${eventId}`);
    } catch (error) {
      console.log('Cancel event reminder error:', error);
    }
  }

  // Get Pending Notifications
  async getPendingNotifications() {
    if (!this.isNative) return [];

    try {
      const notifications = await LocalNotifications.getPending();
      return notifications.notifications;
    } catch (error) {
      console.log('Get pending notifications error:', error);
      return [];
    }
  }

  // Clear All Notifications
  async clearAllNotifications() {
    if (!this.isNative) return;

    try {
      const pending = await this.getPendingNotifications();
      if (pending.length > 0) {
        const ids = pending.map(n => ({ id: n.id }));
        await LocalNotifications.cancel({ notifications: ids });
      }
      console.log('All notifications cleared');
    } catch (error) {
      console.log('Clear all notifications error:', error);
    }
  }

  // Test Notification
  async sendTestNotification() {
    if (!this.isNative) return;

    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: 1,
            title: 'Devimana Calendar',
            body: 'Test notification from your Devimana Calendar app!',
            schedule: { at: new Date(Date.now() + 1000 * 5) }, // 5 seconds from now
            sound: 'default'
          }
        ]
      });
      console.log('Test notification scheduled');
    } catch (error) {
      console.log('Test notification error:', error);
    }
  }

  // Get Push Token
  getPushToken() {
    return this.pushToken;
  }

  // Check if notifications are supported
  isSupported() {
    return this.isNative && this.isInitialized;
  }
}

export default new NotificationService();